// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

/**
 * COB-263 — the `postMessage` protocol for the verifier-served confirm
 * embed. USER-APP (parent/host) COPY.
 *
 * This is a hand-mirrored copy of the verifier's
 * `web/verifier-spa/src/embed-protocol.ts` — copied, NOT imported (no
 * cross-repo source deps). The type definitions, the `EMBED_PROTOCOL`
 * string, and the two directions of message MUST stay byte-compatible
 * with the verifier's copy or the handshake silently breaks; when you
 * change one, change both.
 *
 * The verifier serves `GET /embed/confirm/{id}` as an `<iframe>` inside
 * the user-app's vault-creation modal. Independence comes from origin
 * isolation: the contract is rendered by the verifier's OWN origin, never
 * by user-app (this gate defends against a compromised user-app — COB-245
 * — so a user-app-rendered contract would defeat the purpose).
 *
 * Direction of trust in this flow is the INVERSE of the `sign-verifier-
 * challenge` popup: here the verifier embed is the CHILD, and the user-app
 * is the PARENT that holds the in-context key-agent and signs the
 * ownership challenge. The embed asks the parent to sign; the parent
 * reconstructs the canonical `cobault-verifier-ownership-v1:{vault}:{nonce}`
 * message itself and NEVER signs a caller-supplied body.
 *
 * This module is TYPES + GUARDS ONLY. Every message carries
 * `type: 'cobault-embed-confirm-v1'`; a receiver MUST also pin
 * `event.origin` to the expected peer origin before trusting a message
 * (the type check alone is not an origin check).
 *
 * The verifier's copy exports the CHILD constructors + the PARENT-message
 * guards (it is the child). This copy is the mirror image: it keeps the
 * shared type layer verbatim, and adds the PARENT constructors + the
 * CHILD-message guards the host needs.
 */

export const EMBED_PROTOCOL = 'cobault-embed-confirm-v1';

// ── child → parent (verifier embed → user-app host) ─────────────────

/** The embed has mounted and attached its listener. Lets the host show a
 *  "loading verifier…" state until the frame is alive. */
export interface EmbedReadyMessage {
  type: typeof EMBED_PROTOCOL;
  kind: 'ready';
  vault_id: string;
}

/** Please sign the ownership challenge for `vault_id` over `nonce_hex`.
 *  The parent reconstructs the canonical message from these two fields —
 *  it does not receive (and must not sign) a pre-built message body. */
export interface EmbedRequestSignatureMessage {
  type: typeof EMBED_PROTOCOL;
  kind: 'request-signature';
  vault_id: string;
  nonce_hex: string;
}

/** Content height changed — the host should resize the iframe to `height`
 *  CSS pixels so the embed never scrolls inside its own frame. */
export interface EmbedResizeMessage {
  type: typeof EMBED_PROTOCOL;
  kind: 'resize';
  height: number;
}

/** The owner confirmed the contract on the verifier. **Advisory only** —
 *  the deposit-address reveal stays gated server-side on the verifier's
 *  ack-status (fail-closed). A spoofed `confirmed` must reveal nothing;
 *  the host uses it purely as a UX signal (close the modal, refetch). */
export interface EmbedConfirmedMessage {
  type: typeof EMBED_PROTOCOL;
  kind: 'confirmed';
  vault_id: string;
  contract_hash_hex: string;
}

/** An unrecoverable error inside the embed (advisory — the host may show
 *  a fallback + the direct verifier link). */
export interface EmbedErrorMessage {
  type: typeof EMBED_PROTOCOL;
  kind: 'error';
  code: string;
  message: string;
}

export type EmbedChildMessage =
  | EmbedReadyMessage
  | EmbedRequestSignatureMessage
  | EmbedResizeMessage
  | EmbedConfirmedMessage
  | EmbedErrorMessage;

// ── parent → child (user-app host → verifier embed) ─────────────────

/** The parent's DER-encoded ECDSA signature over the canonical ownership
 *  message it reconstructed for `(vault_id, nonce_hex)`. */
export interface EmbedSignatureMessage {
  type: typeof EMBED_PROTOCOL;
  kind: 'signature';
  vault_id: string;
  nonce_hex: string;
  signature_der_hex: string;
}

/** The parent could not produce a signature (user declined, no session,
 *  key-agent locked, etc.). `reason` is a short machine-ish code the
 *  embed surfaces without leaking parent-side detail. */
export interface EmbedSignatureErrorMessage {
  type: typeof EMBED_PROTOCOL;
  kind: 'signature-error';
  vault_id: string;
  nonce_hex: string;
  reason: string;
}

export type EmbedParentMessage =
  | EmbedSignatureMessage
  | EmbedSignatureErrorMessage;

// ── shared helpers ──────────────────────────────────────────────────

const HEX_RE = /^[0-9a-f]+$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

// ── parent-message constructors (used by the user-app host) ─────────

/** The DER-signature reply the host posts back to the embed after it
 *  reconstructs and signs the canonical ownership message itself. */
export function signatureMessage(
  vaultId: string,
  nonceHex: string,
  signatureDerHex: string,
): EmbedSignatureMessage {
  return {
    type: EMBED_PROTOCOL,
    kind: 'signature',
    vault_id: vaultId,
    nonce_hex: nonceHex,
    signature_der_hex: signatureDerHex,
  };
}

/** The failure reply the host posts back when it will not / cannot sign. */
export function signatureErrorMessage(
  vaultId: string,
  nonceHex: string,
  reason: string,
): EmbedSignatureErrorMessage {
  return {
    type: EMBED_PROTOCOL,
    kind: 'signature-error',
    vault_id: vaultId,
    nonce_hex: nonceHex,
    reason,
  };
}

// ── child → parent guards (used by the user-app host) ───────────────
//
// Every guard checks the protocol type + kind + field shapes. Callers
// MUST additionally have checked `event.origin === <expected verifier
// origin>` before calling these — a matching shape from the wrong origin
// is still hostile.

/** True iff `value` is any well-formed child envelope (protocol type +
 *  a string `kind`). Used to decide whether a `message` event is one of
 *  ours at all before dispatching on `kind`. */
export function isEmbedChildMessage(value: unknown): value is EmbedChildMessage {
  if (!isRecord(value)) return false;
  return value.type === EMBED_PROTOCOL && typeof value.kind === 'string';
}

/** True iff `value` is a `ready` envelope. */
export function isEmbedReadyMessage(value: unknown): value is EmbedReadyMessage {
  if (!isRecord(value)) return false;
  return (
    value.type === EMBED_PROTOCOL &&
    value.kind === 'ready' &&
    typeof value.vault_id === 'string'
  );
}

/**
 * True iff `value` is a well-formed `request-signature` envelope: a string
 * `vault_id` and a lowercase-hex `nonce_hex`. This is a SHAPE check only —
 * the host separately pins the requested `vault_id` to the vault the modal
 * is confirming and reconstructs the canonical message itself (it never
 * signs a caller-supplied body).
 */
export function isEmbedRequestSignature(
  value: unknown,
): value is EmbedRequestSignatureMessage {
  if (!isRecord(value)) return false;
  return (
    value.type === EMBED_PROTOCOL &&
    value.kind === 'request-signature' &&
    typeof value.vault_id === 'string' &&
    typeof value.nonce_hex === 'string' &&
    value.nonce_hex.length > 0 &&
    HEX_RE.test(value.nonce_hex)
  );
}

/** True iff `value` is a `resize` envelope carrying a finite, non-negative
 *  height. */
export function isEmbedResizeMessage(value: unknown): value is EmbedResizeMessage {
  if (!isRecord(value)) return false;
  return (
    value.type === EMBED_PROTOCOL &&
    value.kind === 'resize' &&
    typeof value.height === 'number' &&
    Number.isFinite(value.height) &&
    value.height >= 0
  );
}

/** True iff `value` is a `confirmed` envelope for exactly `vault`. The
 *  signal is advisory — the reveal stays server-gated — so this only
 *  needs to bind the vault the host is confirming. */
export function isEmbedConfirmedFor(
  value: unknown,
  vault: string,
): value is EmbedConfirmedMessage {
  if (!isRecord(value)) return false;
  return (
    value.type === EMBED_PROTOCOL &&
    value.kind === 'confirmed' &&
    value.vault_id === vault &&
    typeof value.contract_hash_hex === 'string'
  );
}

/** True iff `value` is an `error` envelope. */
export function isEmbedErrorMessage(value: unknown): value is EmbedErrorMessage {
  if (!isRecord(value)) return false;
  return (
    value.type === EMBED_PROTOCOL &&
    value.kind === 'error' &&
    typeof value.code === 'string' &&
    typeof value.message === 'string'
  );
}
